import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { ActionIcon, Textarea } from '@mantine/core'
import { IconPlayerStopFilled, IconSend } from '@tabler/icons-react'
import { VoiceInputButton, type VoiceInputButtonHandle } from '@/components/ui'

type InputBarProps = {
  streaming?: boolean
  focusRequest?: number
  onSubmit: (text: string) => Promise<'sent' | 'already_streaming'>
  onCancel?: () => void
  /** 'light' (default) = Assistant page, unchanged. 'dark' = in-call rail:
      compact dark skin, no voice input, no disclaimer footer. */
  variant?: 'light' | 'dark'
}

// Per-variant style maps (FollowupVisitTopics pattern). Dark input classNames
// need `!`: the mantine layer sits above utilities (index.css layer order), so
// unsuffixed font/color utilities lose on Mantine inputs.
const variantStyles = {
  light: {
    frame: 'border-t border-[#e2e8f0] bg-white px-5 py-3 sm:px-20 sm:pl-15',
    box: 'mx-auto flex items-end gap-2 rounded-2xl border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 focus-within:border-[#2563eb] focus-within:bg-white',
    input: undefined as string | undefined,
    placeholder: 'Ask BeAssistant a question…',
  },
  dark: {
    frame: 'border-t border-white/[0.07] px-[14px] py-[10px]',
    box: 'flex items-end gap-2 rounded-xl border border-[#1e293b] bg-[#0b1220] px-2.5 py-1.5 focus-within:border-slate-500',
    input: 'text-[12px]! text-slate-200! placeholder:text-slate-500!',
    placeholder: 'Ask about this patient…',
  },
}

export const InputBar = ({
  streaming = false,
  focusRequest,
  onSubmit,
  onCancel,
  variant = 'light',
}: InputBarProps) => {
  const styles = variantStyles[variant]
  const [value, setValue] = useState('')
  const [micError, setMicError] = useState<string | null>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const lastFocusRequestRef = useRef<number | undefined>(undefined)
  const voiceInputRef = useRef<VoiceInputButtonHandle>(null)

  useEffect(() => {
    if (lastFocusRequestRef.current === focusRequest) return
    lastFocusRequestRef.current = focusRequest
    inputRef.current?.focus()
  }, [focusRequest])

  const submit = async () => {
    const trimmed = value.trim()
    if (!trimmed || streaming) return
    voiceInputRef.current?.stop()
    setMicError(null)
    // Only clear the input once we know the message was actually sent. If the
    // server reports the conversation is already streaming (another tab is
    // driving the turn), preserve the typed text so the user can re-send when
    // the in-flight turn finishes.
    const result = await onSubmit(trimmed)
    if (result === 'sent') setValue('')
  }

  // Enter only submits — never cancels. Cancel must be an explicit click so
  // it isn't triggered by a stray keypress mid-response.
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void submit()
    }
  }

  return (
    <div className={styles.frame}>
      <div className={styles.box}>
        <Textarea
          ref={inputRef}
          autosize
          minRows={1}
          maxRows={4}
          variant="unstyled"
          className="flex-1"
          classNames={styles.input ? { input: styles.input } : undefined}
          placeholder={styles.placeholder}
          value={value}
          onChange={(e) => setValue(e.currentTarget.value)}
          onKeyDown={onKeyDown}
        />
        {variant === 'light' && (
          <VoiceInputButton
            ref={voiceInputRef}
            value={value}
            onChange={setValue}
            disabled={streaming}
            onErrorChange={setMicError}
          />
        )}
        {streaming ? (
          <ActionIcon
            color="red"
            variant="filled"
            radius="xl"
            size={variant === 'dark' ? 'md' : 'lg'}
            disabled={!onCancel}
            onClick={onCancel}
            aria-label="Cancel response"
          >
            <IconPlayerStopFilled size={16} />
          </ActionIcon>
        ) : (
          <ActionIcon
            color="blue"
            variant="filled"
            radius="xl"
            size={variant === 'dark' ? 'md' : 'lg'}
            disabled={value.trim().length === 0}
            onClick={() => void submit()}
            aria-label="Send"
          >
            <IconSend size={16} />
          </ActionIcon>
        )}
      </div>
      {micError && (
        <p className="mx-auto mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-center text-[11px] text-red-700">
          {micError}
        </p>
      )}
      {variant === 'light' && (
        <p className="mx-auto mt-2 text-center text-[11px] text-slate-500">
          <span className="font-semibold">
            BeAssistant can make mistakes — please verify results before acting on them.
          </span>{' '}
          BeAssistant is read-only and cannot modify patient records.
        </p>
      )}
    </div>
  )
}
